"use client";

import * as React from "react";
import { motion } from "motion/react";
import { cn } from "../lib/cn";
import { durations } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * AuthErrorAlert - the inline error box every auth screen re-hand-rolled.
 *
 * The unified auth screens repeated the exact same literal four times:
 * `bg-[rgba(192,87,70,0.10)] border border-[rgba(192,87,70,0.4)]
 * text-[#a8482e]` with `role="alert"`. This is that box, once, with the raw
 * terracotta hex replaced by the brand `--destructive` token (so it retints
 * with the theme and reads on-brand). It always carries `role="alert"` so a
 * screen reader announces the failure the moment it mounts.
 *
 * Decoding contract (documented here so consumers keep it): the unified
 * ResetPassword normalizes RTK Query errors through `toError`, so the backend
 * payload lives on `error.data.detail` (not `error.response.data`). Read that
 * in the consumer's catch, then pass the string here - this atom only renders.
 *
 * Motion: enters/exits with a ~0.15s opacity fade (matching AuthModal's error
 * pattern). Honors `prefers-reduced-motion` via `useReducedMotion()` - when the
 * user prefers reduced motion it mounts at full opacity with no tween. Wrap in
 * `<AnimatePresence>` at the call site for the exit fade.
 *
 *   <AnimatePresence>
 *     {error && <AuthErrorAlert key="err">{error}</AuthErrorAlert>}
 *   </AnimatePresence>
 */
export interface AuthErrorAlertProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "children"
  > {
  /** The error copy. Alias for `children`; either works. */
  message?: React.ReactNode;
  children?: React.ReactNode;
  /** Animate the opacity fade on mount/exit. Default true; reduced-motion forces static. */
  animate?: boolean;
}

export const AuthErrorAlert = React.forwardRef<HTMLDivElement, AuthErrorAlertProps>(
  ({ className, message, children, animate = true, ...props }, ref) => {
    const reduced = useReducedMotion();
    // Treat `null` (pre-resolve) as "motion allowed" so SSR content does not
    // animate-then-snap once the client resolves, per the hook's guidance.
    const motionAllowed = reduced === false || reduced === null;
    const animated = animate && motionAllowed;

    const boxClass = cn(
      "rounded-lg border px-3 py-[9px] text-[13px]",
      "border-destructive/40 bg-destructive/10 text-destructive",
      className,
    );

    const content = message ?? children;

    if (animated) {
      return (
        <motion.div
          ref={ref}
          role="alert"
          className={boxClass}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: durations.hover }}
        >
          {content}
        </motion.div>
      );
    }

    return (
      <div ref={ref} role="alert" className={boxClass} {...props}>
        {content}
      </div>
    );
  },
);
AuthErrorAlert.displayName = "AuthErrorAlert";
